'use client';
import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Star } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabaseClient';

interface ArticleRatingProps {
  articleId: string;
  className?: string;
}

function getSessionId(): string {
  if (typeof window === 'undefined') return '';
  let id = localStorage.getItem('article_rating_session_id');
  if (!id) {
    id = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('article_rating_session_id', id);
  }
  return id;
}

export function ArticleRating({ articleId, className }: ArticleRatingProps) {
  const [starRating, setStarRating] = useState<number>(0);
  const [hoveredStar, setHoveredStar] = useState<number>(0);
  const { toast } = useToast();
  const sessionId = getSessionId();

  useEffect(() => {
    (supabase as any)
      .from('article_ratings')
      .select('rating_type, star_value')
      .eq('article_slug', articleId)
      .eq('session_id', sessionId)
      .maybeSingle()
      .then(({ data }: { data: any }) => {
        if (data && data.rating_type === 'star' && data.star_value) {
          setStarRating(data.star_value);
        }
      });
  }, [articleId, sessionId]);

  const saveToDb = async (starValue: number) => {
    try {
      const { data: existing } = await (supabase as any)
        .from('article_ratings')
        .select('id')
        .eq('article_slug', articleId)
        .eq('session_id', sessionId)
        .maybeSingle();

      if (existing) {
        await (supabase as any)
          .from('article_ratings')
          .update({ rating_type: 'star', star_value: starValue })
          .eq('id', existing.id);
      } else {
        await (supabase as any)
          .from('article_ratings')
          .insert({
            article_slug: articleId,
            rating_type: 'star',
            star_value: starValue,
            session_id: sessionId,
          });
      }
    } catch (error) {
      console.error('Error saving rating to DB:', error);
    }
  };

  const handleStarRating = (rating: number) => {
    setStarRating(rating);
    saveToDb(rating);

    toast({
      title: "Thanks for rating this article!",
      description: `You gave this article ${rating} star${rating !== 1 ? 's' : ''}.`,
    });
  };

  return (
    <div className={cn('border-t pt-6 mt-8', className)}>
      <div className="text-center space-y-4">
        <h3 className="text-lg font-semibold">Rate this article</h3>

        <div className="flex items-center justify-center gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              aria-label={`Rate ${star} star${star !== 1 ? 's' : ''}`}
              className="p-1 transition-colors"
              onMouseEnter={() => setHoveredStar(star)}
              onMouseLeave={() => setHoveredStar(0)}
              onClick={() => handleStarRating(star)}
            >
              <Star 
                className={cn(
                  'w-6 h-6 transition-colors',
                  (hoveredStar >= star || starRating >= star)
                    ? 'fill-yellow-400 text-yellow-400' 
                    : 'text-muted-foreground hover:text-yellow-400'
                )}
              />
            </button>
          ))}
        </div>
        {starRating > 0 && (
          <p className="text-sm text-muted-foreground">
            You rated this article {starRating} out of 5 stars
          </p>
        )}
      </div>
    </div>
  );
}
